import { getOrderExportTotals, getExportProductIvaPercent, getExportProductTaxAmount } from './exportOrderTotalsUtils'

export type ColumnCategory = 'pedido' | 'cliente' | 'producto' | 'envio' | 'pago' | 'tienda' | 'agente'

export interface ColumnDefinition {
  key: string
  label: string
  category: ColumnCategory
  getValue: (order: any, product: any) => string | number
  defaultSelected?: boolean
  width?: number
}

export const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  // Categoría: Pedido
  {
    key: 'fecha_creacion',
    label: 'Fecha Creación',
    category: 'pedido',
    getValue: (order) => new Date(order.date_created).toLocaleDateString(),
    defaultSelected: true,
    width: 15
  },
  {
    key: 'id_pedido',
    label: 'ID Pedido',
    category: 'pedido',
    getValue: (order) => order.id,
    defaultSelected: true,
    width: 10
  },
  {
    key: 'orders_id',
    label: 'Origen',
    category: 'pedido',
    getValue: (order) => order.orders_id || '',
    defaultSelected: true,
    width: 12
  },
  {
    key: 'origin_order_id',
    label: 'ID Origen',
    category: 'pedido',
    getValue: (order) => order.origin_order_id || '',
    defaultSelected: false,
    width: 15
  },
  {
    key: 'id_remision',
    label: 'ID Remisión',
    category: 'pedido',
    getValue: (order) => order.id_remision || '',
    defaultSelected: false,
    width: 12
  },
  {
    key: 'estado',
    label: 'Estado',
    category: 'pedido',
    getValue: (order) => order.status || '',
    defaultSelected: true,
    width: 15
  },
  {
    key: 'total',
    label: 'Total',
    category: 'pedido',
    getValue: (order) => order.total || '0.00',
    defaultSelected: false,
    width: 12
  },
  {
    key: 'subtotal',
    label: 'Subtotal',
    category: 'pedido',
    getValue: (order) => getOrderExportTotals(order).subtotal,
    defaultSelected: false,
    width: 12
  },
  {
    key: 'total_descuento',
    label: 'Total Descuento',
    category: 'pedido',
    getValue: (order) => getOrderExportTotals(order).total_descuento,
    defaultSelected: false,
    width: 14
  },
  {
    key: 'total_impuesto',
    label: 'Total Impuesto',
    category: 'pedido',
    getValue: (order) => getOrderExportTotals(order).total_impuesto,
    defaultSelected: false,
    width: 14
  },
  {
    key: 'total_iva_pedido',
    label: 'Total IVA Pedido',
    category: 'pedido',
    getValue: (order) => getOrderExportTotals(order).total_impuesto,
    defaultSelected: false,
    width: 16
  },
  {
    key: 'date_created',
    label: 'Fecha Completa',
    category: 'pedido',
    getValue: (order) => new Date(order.date_created).toISOString(),
    defaultSelected: false,
    width: 20
  },
  
  // Categoría: Cliente
  {
    key: 'first_name',
    label: 'Nombre',
    category: 'cliente',
    getValue: (order) => order.first_name || '',
    defaultSelected: false,
    width: 15
  },
  {
    key: 'last_name',
    label: 'Apellido',
    category: 'cliente',
    getValue: (order) => order.last_name || '',
    defaultSelected: false,
    width: 15
  },
  {
    key: 'document',
    label: 'Documento',
    category: 'cliente',
    getValue: (order) => order.document || '',
    defaultSelected: false,
    width: 15
  },
  {
    key: 'customer_id',
    label: 'ID Cliente',
    category: 'cliente',
    getValue: (order) => order.customer_id || '',
    defaultSelected: false,
    width: 12
  },
  {
    key: 'billing_city',
    label: 'Ciudad Facturación',
    category: 'cliente',
    getValue: (order) => order.billing_city || '',
    defaultSelected: false,
    width: 18
  },
  {
    key: 'shipping_city',
    label: 'Ciudad Envío',
    category: 'cliente',
    getValue: (order) => order.shipping_city || '',
    defaultSelected: false,
    width: 18
  },
  
  // Categoría: Producto
  {
    key: 'nombre_producto',
    label: 'Nombre Producto',
    category: 'producto',
    getValue: (order, product) => product.nombre || product.name || '',
    defaultSelected: true,
    width: 30
  },
  {
    key: 'sku',
    label: 'SKU',
    category: 'producto',
    getValue: (order, product) => product.sku || '',
    defaultSelected: true,
    width: 15
  },
  {
    key: 'id_producto',
    label: 'ID Producto',
    category: 'producto',
    getValue: (order, product) => product.id_producto || '',
    defaultSelected: false,
    width: 12
  },
  {
    key: 'cantidad',
    label: 'Cantidad',
    category: 'producto',
    getValue: (order, product) => product.quantity || 1,
    defaultSelected: true,
    width: 10
  },
  {
    key: 'precio',
    label: 'Precio',
    category: 'producto',
    getValue: (order, product) => product.precio || '0.00',
    defaultSelected: false,
    width: 12
  },
  {
    key: 'tasa_impuesto',
    label: 'IVA %',
    category: 'producto',
    getValue: (order, product) => getExportProductIvaPercent(order, product),
    defaultSelected: false,
    width: 10
  },
  {
    key: 'tasa_impuesto_valor',
    label: 'IVA valor',
    category: 'producto',
    getValue: (order, product) => getExportProductTaxAmount(order, product),
    defaultSelected: false,
    width: 14
  },
  {
    key: 'componentes',
    label: 'Componentes',
    category: 'producto',
    getValue: (order, product) => 
      product.components ? product.components.map((c: any) => c.sku).join(', ') : '',
    defaultSelected: true,
    width: 30
  },
  {
    key: 'ean12',
    label: 'EAN12',
    category: 'producto',
    getValue: (order, product) => product.ean12 || '',
    defaultSelected: false,
    width: 15
  },
  {
    key: 'peso',
    label: 'Peso',
    category: 'producto',
    getValue: (order, product) => product.peso || '',
    defaultSelected: false,
    width: 10
  },
  {
    key: 'dimensiones',
    label: 'Dimensiones (L×A×H)',
    category: 'producto',
    getValue: (order, product) => {
      const l = product.longitud || ''
      const a = product.ancho || ''
      const h = product.alto || ''
      return l && a && h ? `${l}×${a}×${h}` : ''
    },
    defaultSelected: false,
    width: 18
  },
  
  // Categoría: Envío
  {
    key: 'guia',
    label: 'Guía',
    category: 'envio',
    getValue: (order) => order.guia || '',
    defaultSelected: true,
    width: 15
  },
  {
    key: 'transportadora',
    label: 'Transportadora',
    category: 'envio',
    getValue: (order) => order.transportadora || '',
    defaultSelected: true,
    width: 20
  },
  {
    key: 'costo_envio',
    label: 'Costo Envío',
    category: 'envio',
    getValue: (order) => order.costo_envio ?? order.extraDetails?.costo_envio ?? '0.00',
    defaultSelected: false,
    width: 12
  },
  {
    key: 'documento1',
    label: 'Documento 1',
    category: 'envio',
    getValue: (order) => order.documento1 || '',
    defaultSelected: false,
    width: 15
  },
  {
    key: 'documento2',
    label: 'Documento 2',
    category: 'envio',
    getValue: (order) => order.documento2 || '',
    defaultSelected: false,
    width: 15
  },
  
  // Categoría: Pago
  {
    key: 'payment_method',
    label: 'Método de Pago',
    category: 'pago',
    getValue: (order) => order.payment_method || '',
    defaultSelected: false,
    width: 18
  },
  {
    key: 'transaction_id',
    label: 'ID Transacción',
    category: 'pago',
    getValue: (order) => order.transaction_id || '',
    defaultSelected: false,
    width: 18
  },
  
  // Categoría: Tienda
  {
    key: 'store_name',
    label: 'Tienda',
    category: 'tienda',
    getValue: (order) => order.store_name || '',
    defaultSelected: true,
    width: 15
  },
  
  // Categoría: Agente
  {
    key: 'agent_id',
    label: 'ID Agente',
    category: 'agente',
    getValue: (order) => order.agent?.id || '',
    defaultSelected: false,
    width: 12
  },
  {
    key: 'agent_first_name',
    label: 'Nombre Agente',
    category: 'agente',
    getValue: (order) => order.agent?.first_name || '',
    defaultSelected: false,
    width: 18
  },
  {
    key: 'agent_last_name',
    label: 'Apellido Agente',
    category: 'agente',
    getValue: (order) => order.agent?.last_name || '',
    defaultSelected: false,
    width: 18
  },
  {
    key: 'agent_full_name',
    label: 'Nombre Completo Agente',
    category: 'agente',
    getValue: (order) => {
      if (!order.agent) return ''
      const firstName = order.agent.first_name || ''
      const lastName = order.agent.last_name || ''
      return `${firstName} ${lastName}`.trim()
    },
    defaultSelected: false,
    width: 25
  }
]

export const getDefaultSelectedColumns = (): string[] => {
  return COLUMN_DEFINITIONS
    .filter(col => col.defaultSelected)
    .map(col => col.key)
}

export const getColumnsByCategory = (): Record<ColumnCategory, ColumnDefinition[]> => {
  const categories: Record<ColumnCategory, ColumnDefinition[]> = {
    pedido: [],
    cliente: [],
    producto: [],
    envio: [],
    pago: [],
    tienda: [],
    agente: []
  }
  
  COLUMN_DEFINITIONS.forEach(col => {
    categories[col.category].push(col)
  })
  
  return categories
}

export const getColumnDefinition = (key: string): ColumnDefinition | undefined => {
  return COLUMN_DEFINITIONS.find(col => col.key === key)
}

